import type { ContextoErro } from '@next/compartilhados/observabilidade'
import { ErroDoServidorPontotel, ErroNoNavegadorPontotel } from '@/tipos/erros/classes'

const objeto = (valor: unknown): Record<string, unknown> => (
  valor !== null && typeof valor === 'object' ? valor as Record<string, unknown> : {}
)
const texto = (valor: unknown): string | undefined => (
  typeof valor === 'string' && valor.trim() ? valor : undefined
)

const mensagemDoCorpo = (corpo: unknown): string | undefined => {
  if (typeof corpo === 'string') {
    // Extrai campos conhecidos de JSON textual, sem serializar documentos ou páginas HTML.
    if (/^\s*\{/.test(corpo)) {
      if (corpo.length > 65536) return undefined
      try { return mensagemDoCorpo(JSON.parse(corpo)) } catch { return undefined }
    }
    return /^\s*[<[]/.test(corpo) ? undefined : texto(corpo)
  }
  const campos = objeto(corpo)
  return [campos.message, campos.mensagem, campos.error, campos.detail, campos.description]
    .map(texto).find(Boolean)
}

const errosConvertidos = new WeakMap<object, Error>()
const originais = new WeakMap<object, object>()

/** Referência privada para deduplicação e diagnóstico; não anexar resposta/cause ao Error enviado ao SDK. */
export const obterErroOriginalLegado = (erro: unknown): unknown => (
  erro !== null && typeof erro === 'object' ? originais.get(erro) : undefined
)

/** Adapta somente respostas HTTP lançadas como objeto, sem alterar o objeto usado pelo fluxo. */
export const converterErroHttpLegado = (erro: unknown, contexto: ContextoErro): unknown => {
  try {
    if (!erro || typeof erro !== 'object' || erro instanceof Error
      || contexto.naturezaFalha !== 'http' || contexto.statusHttp === undefined) return erro
    const existente = errosConvertidos.get(erro)
    if (existente) return existente

    const resposta = objeto(erro)
    const mensagem = texto(resposta.message)
      ?? mensagemDoCorpo(resposta.data)
      ?? mensagemDoCorpo(resposta.body)
      ?? `HTTP ${contexto.statusHttp}`
    const convertido = new Error(mensagem)
    convertido.name = texto(resposta.name) ?? 'ErroHttpLegado'
    if (texto(resposta.stack)) convertido.stack = resposta.stack as string
    else Error.captureStackTrace?.(convertido, converterErroHttpLegado)
    // Não anexar resposta/cause: headers, payload e corpo completo ficam fora do diagnóstico.
    errosConvertidos.set(erro, convertido)
    originais.set(convertido, erro)
    return convertido
  } catch {
    // Getters ou objetos incomuns não podem interromper o fluxo da aplicação.
    return erro
  }
}

/** Adaptação de diagnóstico para todas as entradas do SDK, sem modificar o erro da aplicação. */
export const projetarErroLegado = (erro: unknown, contexto: ContextoErro) => {
  const original = obterErroOriginalLegado(erro) ?? erro
  const convertido = converterErroHttpLegado(erro, contexto)
  const tipo = (erro instanceof ErroDoServidorPontotel || erro instanceof ErroNoNavegadorPontotel) && erro.name === 'Error'
    ? erro.nome : undefined
  return {
    erro: convertido,
    ...(tipo ? { tipo } : {}),
    ...(convertido !== original ? { origemStack: texto(objeto(original).stack) ? 'original' as const : 'captura' as const } : {}),
  }
}
